import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const InventoryStatusPanel = ({ inventoryStatus }) => {
  // Group inventory by category and count items
  const categoryBreakdown = React.useMemo(() => {
    if (!inventoryStatus || !Array.isArray(inventoryStatus)) return [];

    const categoryMap = {};
    
    inventoryStatus?.forEach((item) => {
      const category = item?.category || 'Uncategorized';
      
      if (!categoryMap?.[category]) {
        categoryMap[category] = {
          name: category,
          itemCount: 0,
          totalQuantity: 0
        };
      }
      
      categoryMap[category].itemCount += 1;
      categoryMap[category].totalQuantity += (item?.quantity || 0);
    });

    return Object.values(categoryMap)?.sort((a, b) => a?.name?.localeCompare(b?.name));
  }, [inventoryStatus]);

  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      'Produce': 'Apple',
      'Dairy': 'Milk',
      'Meat': 'Beef',
      'Seafood': 'Fish',
      'Grains': 'Wheat',
      'Canned Goods': 'Package',
      'Beverages': 'Coffee',
      'Snacks': 'Cookie',
      'Condiments': 'Droplet',
      'Spices': 'Flame',
      'Frozen': 'Snowflake',
      'Bakery': 'Croissant'
    };
    return iconMap?.[categoryName] || 'Package';
  };

  if (!categoryBreakdown || categoryBreakdown?.length === 0) {
    return (
      <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-elevation-2">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
            Inventory Breakdown
          </h3>
          <Icon name="Package" size={24} color="var(--color-primary)" />
        </div>
        <div className="text-center py-8">
          <p className="text-muted-foreground">No inventory items yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-elevation-2">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
          Inventory Breakdown
        </h3>
        <Icon name="Package" size={24} color="var(--color-primary)" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {categoryBreakdown?.map((category, index) => (
          <div key={index} className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Icon 
                    name={getCategoryIcon(category?.name)} 
                    size={20} 
                    color="var(--color-primary)"
                  />
                </div>
                <div>
                  <h4 className="text-sm md:text-base font-medium text-foreground">
                    {category?.name}
                  </h4>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-semibold text-foreground">
                  {category?.itemCount}
                </p>
                <p className="text-xs text-muted-foreground">
                  {category?.itemCount === 1 ? 'item' : 'items'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-medium text-muted-foreground">
                  {category?.totalQuantity}
                </p>
                <p className="text-xs text-muted-foreground">total qty</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InventoryStatusPanel;